/**
 * THE CACHE KEY REGISTRY — every key in the app is defined exactly once, here.
 *
 * The reason is refactor safety, not tidiness. A producer (`useQuery`) and an invalidator
 * (`invalidateQueries` after a mutation) have to agree on a key, and when they are written
 * as inline arrays in two different files they agree only until someone renames one of
 * them. Nothing fails: the query keeps caching, the invalidation keeps "succeeding", and a
 * screen quietly shows stale data — which on a phone people read as the app being broken.
 *
 * Sharing one symbol makes that rename impossible to get half-right, and `no-inline-query-keys`
 * (eslint-rules/) fails the build if anyone writes a key by hand.
 *
 * SHAPE: broadest first, so `invalidateQueries({ queryKey: queryKeys.notes.all })` catches
 * every list and detail beneath it. That prefix relationship IS the invalidation design —
 * treat the nesting as meaningful, not decorative.
 */
export const queryKeys = {
  session: {
    all: ["session"] as const,
    current: () => ["session", "current"] as const,
  },
  notes: {
    all: ["notes"] as const,
    /**
     * ONE entry for the whole list, however many pages are loaded. The cursor lives in the
     * infinite query's page params, never here: a cursor in the key makes every page its
     * own cache entry, so an invalidation reaches one of them and an optimistic insert on
     * page one is stranded there while page two carries a stale copy of the same rows.
     */
    list: () => ["notes", "list"] as const,
    detail: (id: string) => ["notes", "detail", id] as const,
  },
  devices: {
    all: ["devices"] as const,
  },
  meta: {
    all: ["meta"] as const,
    minimumVersion: () => ["meta", "minimum-version"] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;
